/**
 * Product Stack — swap base variants for real stack variants on add to cart.
 *
 * The merchandised "base" product is an aggregator: its variants are the union
 * of its yearly "stack" products' option combinations and its inventory is the
 * sum of theirs. Only stack products hold real SKUs, and the 3PL only knows
 * stack SKUs — so the real stack variant has to be what lands in the cart.
 *
 * Interception happens at the network layer on purpose. The theme adds to cart
 * from at least six surfaces (PDP form, quick view, quick-add, bundle buy
 * buttons, sticky ATC, React PLP quick-shop), so wrapping `fetch` covers all of
 * them — including the Vite bundles — instead of editing each one.
 *
 * A Shopify cart does not reserve inventory and the allocate endpoint is
 * stateless, so live `available` cannot see what a previous add already claimed.
 * Every allocate call therefore carries a freshly read `/cart.js` snapshot as
 * repeated `consumed=<variantId>:<units>` params. Without it, a second add of
 * the same product is allocated onto the units the first add took and
 * `/cart/add.js` answers 422.
 *
 * FAILURE HANDLING is theme-configurable (see `[data-product-stack-settings]`,
 * emitted by snippets/scripts.liquid from the "Product Stack" settings):
 *
 *   1. RETRY. A *transient* allocate failure — network error, timeout, 5xx,
 *      429/408 — is retried `retries` times with `retryDelayMs` between
 *      attempts, on the same cart snapshot (nothing was added in between).
 *      Deterministic failures (400/401/403, a 200 without `cartConsumed`, a
 *      malformed body) are never retried: they would fail the same way again
 *      and only leave the button dead for longer.
 *   2. POLICY, once retries are exhausted (and on every other fallback door
 *      below). `failureMode: 'reject'` (the default) adds nothing and shows the
 *      cart error for any line the theme KNOWS is a base variant — a 'base'
 *      role marker, a Liquid base flag, or an earlier allocation in this same
 *      request. `failureMode: 'base'` re-issues the caller's original,
 *      unmodified request instead, so the base variant lands in the cart and
 *      the app rewrites it to stack lines after the order is placed.
 *
 *      A line of UNKNOWN role always falls open, whatever the mode: allocate's
 *      404 is how an ordinary product is recognised, so with the app down the
 *      hijack cannot tell plain from base, and the React PLP quick-shop sends
 *      no role marker. Rejecting unknowns would take every ordinary product's
 *      add-to-cart down with the app.
 *
 * Independently of the mode, a fallback is BARRED once the cart already holds
 * stack lines of the same family: a base line on top of them is an oversell
 * *plus* a post-order conversion the app has to absorb — strictly worse than a
 * clear error, so that case adds nothing and shows the cart error. Three cases
 * are not failures at all:
 *   - allocate 404       → not a base variant; pass straight through.
 *   - allocated: 0       → genuinely out of stock; add nothing and say so.
 *   - reason: CART_LIMIT → the shopper's own cart holds every available unit.
 *                          NOT a sellout: different copy, and never a fallback.
 *
 * Only loaded when `settings.product_stack_hijack_enabled` is on (see
 * snippets/scripts.liquid), so the "off" state costs zero bytes.
 *
 * The pure helpers below are exported and imported directly by
 * test-harness/pdp/product-stack-hijack.mjs — no mirrored copy to keep in sync.
 */

/** App Proxy allocation endpoint. Same-origin, so no headers or credentials. */
export const ALLOCATE_PATH = '/apps/stacks/allocate';
/** Budget for one allocate round trip before we give up and add the base. */
export const ALLOCATE_TIMEOUT_MS = 3000;
/** AJAX cart endpoint. The theme is the only thing that can read the cart. */
export const CART_PATH = '/cart.js';
/** Budget for the cart read that precedes an allocate round trip. */
export const CART_TIMEOUT_MS = 2000;
/** Cart lines reported per allocate call. Must match the endpoint's own cap. */
export const SNAPSHOT_CAP = 250;
/** Hidden line-item properties — a leading underscore keeps them off the UI. */
export const STACK_LABEL_PROPERTY = '_stack_label';
export const STACK_BASE_VARIANT_PROPERTY = '_stack_base_variant';
/** Transient add-path metadata — stripped before Shopify sees the payload. */
export const PRODUCT_STACK_ROLE_PROPERTY = '_product_stack_role';

/**
 * Failure-handling settings, mirrored from config/settings_schema.json. These
 * are what the runtime uses when the settings script tag is missing or
 * unreadable, so they MUST equal the schema defaults.
 */
export const DEFAULT_SETTINGS = Object.freeze({
  retries: 2,
  retryDelayMs: 500,
  /** @type {'reject'|'base'} */
  failureMode: 'reject',
});
/** Schema `max` for the retries range. */
export const RETRIES_MAX = 5;
/** Schema `max` for the retry-delay range. */
export const RETRY_DELAY_MAX_MS = 3000;
const FAILURE_MODES = ['reject', 'base'];

/** Form fields that are not line data and must survive the rewrite. */
const FORM_PASSTHROUGH_KEYS = ['sections', 'sections_url', 'form_type', 'utf8'];
/** Of those, the ones the JSON AJAX cart API actually understands. */
const JSON_PASSTHROUGH_KEYS = ['sections', 'sections_url'];

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

/**
 * True for `/cart/add` and `/cart/add.js`, including locale-prefixed roots.
 * Query strings and origins are ignored; anything else passes through.
 * @param {unknown} input
 * @returns {boolean}
 */
export function isCartAddUrl(input) {
  if (input == null) return false;
  const raw = String(input);
  if (!raw) return false;

  let pathname = raw;
  try {
    pathname = new URL(raw, 'http://theme.invalid').pathname;
  } catch {
    pathname = raw.split('?')[0];
  }

  return /\/cart\/add(\.js)?$/.test(pathname);
}

/**
 * Force the AJAX endpoint. `/cart/add` answers a redirect to the cart page,
 * `/cart/add.js` answers JSON — and we always re-issue as JSON, so a caller
 * that used the non-.js form must still be sent to the .js one.
 * @param {string} url
 * @returns {string}
 */
export function toCartAddJsUrl(url) {
  return String(url).replace(/(\/cart\/add)(?!\.js)(?=$|[?#])/, '$1.js');
}

/**
 * @param {unknown} value
 * @returns {number} A positive integer quantity, defaulting to 1.
 */
function normalizeQuantity(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return Math.floor(parsed);
}

/**
 * Pull transient stack-role metadata off a parsed line. Mutates `properties`.
 * @param {object} line
 * @returns {'base'|'plain'|undefined}
 */
function extractStackRoleFromLine(line) {
  const properties = line.properties && typeof line.properties === 'object' ? line.properties : {};
  const raw = properties[PRODUCT_STACK_ROLE_PROPERTY];
  if (raw != null) delete properties[PRODUCT_STACK_ROLE_PROPERTY];
  line.properties = properties;
  if (raw === 'base' || raw === 'plain') {
    line.stackRole = raw;
    return raw;
  }
  return undefined;
}

/**
 * True when every line carried an explicit plain role marker.
 * @param {Array<object>} lines
 * @returns {boolean}
 */
export function allLinesMarkedPlain(lines) {
  return lines.length > 0 && lines.every((line) => line.stackRole === 'plain');
}

/**
 * Normalize a FormData-style add into line data plus passthrough params.
 * Accepts any iterable of [key, value] so the harness can pass plain arrays.
 * @param {Iterable<[string, unknown]>} entries
 * @returns {{lines: Array<object>, passthrough: Record<string, string>, hadRoleMarkers: boolean}}
 */
export function parseFormEntries(entries) {
  const line = { id: null, quantity: 1, properties: {}, sellingPlan: null };
  /** @type {Record<string, string>} */
  const passthrough = {};
  let hadRoleMarkers = false;

  for (const [key, value] of entries) {
    if (key === 'id') {
      if (value != null && value !== '') line.id = String(value);
    } else if (key === 'quantity') {
      line.quantity = normalizeQuantity(value);
    } else if (key === 'selling_plan') {
      if (value != null && value !== '') line.sellingPlan = String(value);
    } else if (FORM_PASSTHROUGH_KEYS.includes(key)) {
      passthrough[key] = String(value);
    } else {
      const match = /^properties\[(.+)\]$/.exec(key);
      // Empty property values are dropped, matching Shopify's own behaviour.
      if (match && value != null && value !== '') line.properties[match[1]] = String(value);
    }
  }

  if (line.id && extractStackRoleFromLine(line)) hadRoleMarkers = true;

  return { lines: line.id ? [line] : [], passthrough, hadRoleMarkers };
}

/**
 * Normalize a JSON add — either `{items: [...]}` (quick-shop, bundles) or a
 * single `{id, quantity}` object — into the same shape as parseFormEntries.
 * @param {unknown} payload
 * @returns {{lines: Array<object>, passthrough: Record<string, string>, hadRoleMarkers: boolean}}
 */
export function parseJsonPayload(payload) {
  const source = payload && typeof payload === 'object' ? /** @type {any} */ (payload) : {};
  /** @type {Record<string, string>} */
  const passthrough = {};
  let hadRoleMarkers = false;
  for (const key of JSON_PASSTHROUGH_KEYS) {
    if (source[key] != null) passthrough[key] = String(source[key]);
  }

  const rawItems = Array.isArray(source.items)
    ? source.items
    : source.id != null
      ? [source]
      : [];

  const lines = rawItems
    .map((item) => {
      const entry = item && typeof item === 'object' ? /** @type {any} */ (item) : {};
      return {
        id: entry.id != null && entry.id !== '' ? String(entry.id) : null,
        quantity: normalizeQuantity(entry.quantity),
        properties:
          entry.properties && typeof entry.properties === 'object' ? { ...entry.properties } : {},
        sellingPlan:
          entry.selling_plan != null && entry.selling_plan !== ''
            ? String(entry.selling_plan)
            : null,
      };
    })
    .filter((line) => line.id);

  for (const line of lines) {
    if (extractStackRoleFromLine(line)) hadRoleMarkers = true;
  }

  return { lines, passthrough, hadRoleMarkers };
}

/**
 * Clamp one integer setting into [0, max]; anything non-numeric is the default.
 * @param {unknown} value
 * @param {number} fallback
 * @param {number} max
 * @returns {number}
 */
function clampSetting(value, fallback, max) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(0, Math.floor(parsed)));
}

/**
 * Coerce whatever Liquid emitted into a complete, in-range settings object.
 * Never throws: a bad or missing field falls back to its default so a broken
 * settings tag degrades to the schema defaults rather than to `undefined`
 * retries or an unrecognised mode.
 * @param {unknown} raw
 * @returns {{retries: number, retryDelayMs: number, failureMode: 'reject'|'base'}}
 */
export function normalizeSettings(raw) {
  const source = raw && typeof raw === 'object' ? /** @type {any} */ (raw) : {};
  const mode = String(source.failureMode ?? '');
  return {
    retries: clampSetting(source.retries, DEFAULT_SETTINGS.retries, RETRIES_MAX),
    retryDelayMs: clampSetting(source.retryDelayMs, DEFAULT_SETTINGS.retryDelayMs, RETRY_DELAY_MAX_MS),
    failureMode: FAILURE_MODES.includes(mode) ? /** @type {'reject'|'base'} */ (mode) : DEFAULT_SETTINGS.failureMode,
  };
}

/**
 * Whether an allocate failure is worth retrying. Transport failures (no HTTP
 * status at all: network down, abort/timeout) and server-side conditions that
 * clear on their own (5xx, 429 rate limit, 408) are transient. Every other
 * status is the backend telling us, deterministically, no — retrying only
 * burns the delay budget.
 * @param {{status?: unknown, error?: unknown}} failure
 * @returns {boolean}
 */
export function isTransientAllocateFailure(failure) {
  const status = Number(failure?.status);
  if (!Number.isInteger(status) || status <= 0) return failure?.error != null;
  return status >= 500 || status === 429 || status === 408;
}

/**
 * Judge an allocate response on its own numbers rather than trusting the
 * `fulfillable` / `partial` booleans, and reject anything self-inconsistent —
 * an unexpected shape is a fail-open, not something to guess at.
 * @param {unknown} response
 * @returns {'complete'|'partial'|'unfulfillable'|'invalid'}
 */
export function classifyAllocation(response) {
  const body = response && typeof response === 'object' ? /** @type {any} */ (response) : null;
  if (!body || !Array.isArray(body.allocations)) return 'invalid';

  const allocated = Number(body.allocated);
  const requested = Number(body.requested);
  if (!Number.isInteger(allocated) || allocated < 0) return 'invalid';
  if (!Number.isInteger(requested) || requested < 1) return 'invalid';
  // The endpoint contract is that it never oversells.
  if (allocated > requested) return 'invalid';

  let total = 0;
  for (const allocation of body.allocations) {
    if (!allocation || typeof allocation !== 'object') return 'invalid';
    const quantity = Number(allocation.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) return 'invalid';
    if (allocation.stackVariantId == null || allocation.stackVariantId === '') return 'invalid';
    total += quantity;
  }
  if (total !== allocated) return 'invalid';

  if (allocated === 0) return 'unfulfillable';
  return allocated < requested ? 'partial' : 'complete';
}

/**
 * A base line becomes one cart item per allocation — under SPLIT_BY_PRIORITY
 * that is several lines for a single requested item, so never collapse to
 * `allocations[0]`. Every resulting line keeps the caller's own properties.
 * @param {object} line
 * @param {any} response
 * @returns {Array<object>}
 */
export function expandLine(line, response) {
  return response.allocations.map((allocation) => {
    const properties = { ...line.properties };
    if (allocation.label != null && allocation.label !== '') {
      properties[STACK_LABEL_PROPERTY] = String(allocation.label);
    }
    properties[STACK_BASE_VARIANT_PROPERTY] = String(line.id);

    /** @type {any} */
    const item = {
      id: String(allocation.stackVariantId),
      quantity: Number(allocation.quantity),
      properties,
    };
    if (line.sellingPlan) item.selling_plan = line.sellingPlan;
    return item;
  });
}

/**
 * Carry a non-base line through untouched.
 * @param {object} line
 * @returns {object}
 */
export function toCartItem(line) {
  /** @type {any} */
  const item = { id: line.id, quantity: line.quantity };
  if (Object.keys(line.properties).length > 0) item.properties = { ...line.properties };
  if (line.sellingPlan) item.selling_plan = line.sellingPlan;
  return item;
}

/**
 * Build the single re-issued `/cart/add.js` body. `sections`/`sections_url` are
 * carried through so the caller still gets `data.sections` and its drawer
 * re-renders exactly as before; `form_type`/`utf8` are form artifacts and are
 * not valid JSON API fields.
 * @param {Array<object>} items
 * @param {Record<string, string>} passthrough
 * @returns {object}
 */
export function buildCartAddBody(items, passthrough) {
  /** @type {any} */
  const body = { items };
  for (const key of JSON_PASSTHROUGH_KEYS) {
    if (passthrough && passthrough[key] != null) body[key] = passthrough[key];
  }
  return body;
}

/**
 * The shopper's cart as `consumed` entries for allocate.
 *
 * Use `item.variant_id`, never `item.id`: on any line carrying line-item
 * properties — which is every line this hijack creates — `item.id` is the string
 * `"<variantId>:<propertiesHash>"`, and the endpoint answers 400 for it.
 *
 * Every line is sent, not just this family's: the endpoint ignores ids it doesn't
 * recognise, and filtering here would be a second, worse copy of that logic.
 * Lines this hijack created are the only ones that can subtract anything though,
 * so they go first and survive the cap on an enormous cart.
 *
 * @param {any} cart Parsed /cart.js, or null when the read failed.
 * @param {Array<string>} [pending] Entries for stack units this request has
 *   already committed to but which are not in the cart yet.
 * @returns {Array<string>} `<variantId>:<units>` entries, capped.
 */
export function buildConsumed(cart, pending) {
  /** @type {Array<string>} */
  const tagged = [];
  /** @type {Array<string>} */
  const rest = [];

  for (const item of (cart && cart.items) || []) {
    if (!item || typeof item !== 'object') continue;
    const variantId = Number(/** @type {any} */ (item).variant_id);
    const quantity = Number(/** @type {any} */ (item).quantity);
    if (!Number.isInteger(variantId) || variantId <= 0) continue;
    if (!Number.isInteger(quantity) || quantity <= 0) continue;

    const properties = /** @type {any} */ (item).properties || {};
    const ours = properties[STACK_LABEL_PROPERTY] || properties[STACK_BASE_VARIANT_PROPERTY];
    (ours ? tagged : rest).push(`${variantId}:${quantity}`);
  }

  return (pending || []).concat(tagged, rest).slice(0, SNAPSHOT_CAP);
}

/**
 * Units of `baseVariantId`'s family already in the cart — i.e. the lines this
 * hijack created for it. The narrowed fail-open rule keys off this: above zero,
 * adding the base variant would oversell.
 * @param {any} cart Parsed /cart.js, or null.
 * @param {unknown} baseVariantId
 * @returns {number}
 */
export function familyUnitsInCart(cart, baseVariantId) {
  const wanted = String(baseVariantId);
  let units = 0;

  for (const item of (cart && cart.items) || []) {
    if (!item || typeof item !== 'object') continue;
    const reference = (/** @type {any} */ (item).properties || {})[STACK_BASE_VARIANT_PROPERTY];
    if (reference == null || String(reference) !== wanted) continue;
    const quantity = Number(/** @type {any} */ (item).quantity);
    if (Number.isInteger(quantity) && quantity > 0) units += quantity;
  }

  return units;
}

/* -------------------------------------------------------------------------- */
/* Runtime                                                                    */
/* -------------------------------------------------------------------------- */

const GLOBAL_KEY = '__productStackHijack';

/**
 * Wrap `scope.fetch` and hook form submits. Exported so the harness can drive
 * the whole flow against a fake scope — the runtime behaviour (fail-open,
 * 404 passthrough, 422 retry) is the part worth testing, not just the parsing.
 * @param {Window & typeof globalThis} scope
 */
export function install(scope) {
  const existing = /** @type {any} */ (scope)[GLOBAL_KEY];
  if (existing && existing.installed) return existing;

  const originalFetch = scope.fetch.bind(scope);

  /** Variant ids Liquid told us are base variants (see product-stack-flag). */
  const knownBaseVariantIds = new Set();
  /** Variant ids allocate answered 404 for — cached for the page's lifetime. */
  const knownPlainVariantIds = new Set();
  const scannedFlags = new WeakSet();

  /**
   * @param {string} message
   * @param {object} [context]
   */
  function logFallback(message, context) {
    // Every fallback is logged so the silent-fallback rate is measurable
    // before launch.
    console.warn(`[ProductStack] ${message}`, context || {});
  }

  /**
   * Liquid's `t` filter HTML-escapes before `| json`, so apostrophes land as
   * `&#39;` inside the JSON string. Undo that so shoppers never see entities.
   * @param {string} value
   * @returns {string}
   */
  function decodeLiquidHtmlEntities(value) {
    if (!value.includes('&')) return value;
    return value
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  /**
   * Failure-handling settings from the theme editor. Read per add, like
   * `strings()`: it is one JSON.parse of a tiny tag, and reading it at install
   * time would freeze whatever was in the DOM before the harness (or a section
   * re-render) could change it.
   */
  function settings() {
    const node = scope.document?.querySelector('[data-product-stack-settings]');
    if (!node) return normalizeSettings(null);
    try {
      return normalizeSettings(JSON.parse(node.textContent || '{}'));
    } catch (error) {
      logFallback('Unreadable settings tag — using defaults', { error: String(error) });
      return normalizeSettings(null);
    }
  }

  function strings() {
    const node = scope.document?.querySelector('[data-product-stack-strings]');
    if (!node) return {};
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      if (!parsed || typeof parsed !== 'object') return {};
      /** @type {Record<string, string>} */
      const out = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'string') out[key] = decodeLiquidHtmlEntities(value);
      }
      return out;
    } catch {
      return {};
    }
  }

  /**
   * Absence of a flag never means "not a base" — it only means this surface
   * didn't render one, so unknown ids still get asked about.
   */
  function scanBaseFlags() {
    const nodes = scope.document?.querySelectorAll('[data-product-stack-config]');
    if (!nodes) return;
    for (const node of nodes) {
      if (scannedFlags.has(node)) continue;
      scannedFlags.add(node);
      try {
        const config = JSON.parse(node.textContent || '{}');
        if (config.isBase !== true || !Array.isArray(config.variantIds)) continue;
        for (const variantId of config.variantIds) knownBaseVariantIds.add(String(variantId));
      } catch (error) {
        logFallback('Unreadable base flag', { error: String(error) });
      }
    }
  }

  /**
   * Read the cart immediately before allocating. Never cached, and never reused
   * across the 422 retry: a rejected add is *not* in the cart, so a stale
   * snapshot double-counts and skews the next allocation the other way.
   *
   * A failed read is not fatal — it degrades to the pre-snapshot behaviour
   * (allocate with no `consumed`), and `blocksFailOpen` tightens up instead.
   * @returns {Promise<any|null>} Parsed /cart.js, or null if it can't be read.
   */
  async function readCart() {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? scope.setTimeout(() => controller.abort(), CART_TIMEOUT_MS) : null;

    try {
      const root = scope.Shopify?.routes?.root || '/';
      const response = await originalFetch(`${root.replace(/\/$/, '')}${CART_PATH}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller ? controller.signal : undefined,
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
      return await response.json();
    } catch (error) {
      logFallback('cart read failed — allocating with no cart snapshot', {
        error: String(error),
      });
      return null;
    } finally {
      if (timer != null) scope.clearTimeout(timer);
    }
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {Array<string>} consumed
   * @param {unknown} [cartToken]
   */
  function allocateUrl(variantId, quantity, consumed, cartToken) {
    const root = scope.Shopify?.routes?.root || '/';
    const base = root.replace(/\/$/, '');

    const query = new URLSearchParams();
    query.set('variantId', String(variantId));
    // Units being added NOW, never the cart total — the cart goes in `consumed`.
    query.set('quantity', String(quantity));
    // Repeated PLAIN keys. `consumed[]=` arrives under a different name, is
    // stripped by the endpoint's param whitelist, and comes back 200 with the
    // old cart-blind allocation — a silent oversell.
    for (const entry of consumed) query.append('consumed', entry);
    // Diagnostics only; sanitized server-side so it can never fail the request.
    if (cartToken) query.set('cartToken', String(cartToken));

    return `${base}${ALLOCATE_PATH}?${query}`;
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {any} cart Parsed /cart.js, or null when the read failed.
   * @param {Array<string>} pending Units this request has already committed to.
   * @returns {Promise<{outcome: 'plain'|'error'|'unfulfillable'|'cartLimit'|'allocated', retryable?: boolean, verdict?: string, body?: any}>}
   *   `retryable` accompanies every 'error' and is what allocateWithRetry keys off.
   */
  async function allocate(variantId, quantity, cart, pending) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? scope.setTimeout(() => controller.abort(), ALLOCATE_TIMEOUT_MS)
      : null;

    try {
      const url = allocateUrl(variantId, quantity, buildConsumed(cart, pending), cart?.token);
      const response = await originalFetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        // The answer is only correct for the snapshot in this query string, so it
        // must never be served from a cache.
        cache: 'no-store',
        signal: controller ? controller.signal : undefined,
      });

      // 404 is the normal answer for every ordinary product in the store.
      if (response.status === 404) return { outcome: 'plain' };

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        logFallback('allocate failed', { variantId, status: response.status, body });
        return {
          outcome: 'error',
          retryable: isTransientAllocateFailure({ status: response.status }),
        };
      }

      const body = await response.json();

      // `cartConsumed` is always present on a 200 from a cart-aware backend, and
      // is how we detect one: the endpoint's validation pipe silently strips
      // params it doesn't know, so an older build answers 200 having ignored the
      // snapshot entirely. Treat it as a failure rather than trust the number.
      if (!body || body.cartConsumed === undefined) {
        logFallback('allocate answered without cartConsumed — backend predates the cart snapshot', {
          variantId,
          body,
        });
        // A contract mismatch, not a blip: the same build answers the same way.
        return { outcome: 'error', retryable: false };
      }
      // Not fatal, but it means the allocation may under-subtract, so it explains
      // a following 422 that would otherwise look like a stock race.
      if (body.consumedTruncated) {
        logFallback('cart snapshot truncated — allocation may under-subtract', { variantId });
      }

      const verdict = classifyAllocation(body);

      if (verdict === 'invalid') {
        logFallback('allocate returned an unexpected shape', {
          variantId,
          status: response.status,
          body,
        });
        return { outcome: 'error', retryable: false };
      }
      if (verdict === 'unfulfillable') {
        // CART_LIMIT is not a sellout: the stacks do hold stock, the shopper's
        // own cart is holding all of it. Different copy, and never a fallback.
        return { outcome: body.reason === 'CART_LIMIT' ? 'cartLimit' : 'unfulfillable', body };
      }
      return { outcome: 'allocated', verdict, body };
    } catch (error) {
      logFallback('allocate errored or timed out', { variantId, error: String(error) });
      return { outcome: 'error', retryable: isTransientAllocateFailure({ error }) };
    } finally {
      if (timer != null) scope.clearTimeout(timer);
    }
  }

  /**
   * `allocate`, re-attempted per the theme's retry settings. Only a transient
   * failure is retried, and the SAME cart snapshot is reused throughout: no add
   * happened between attempts, so the snapshot is still exactly right and a
   * re-read would only add latency to an already-slow path.
   * @param {string} variantId
   * @param {number} quantity
   * @param {any} cart
   * @param {Array<string>} pending
   */
  async function allocateWithRetry(variantId, quantity, cart, pending) {
    const { retries, retryDelayMs } = settings();
    let result = await allocate(variantId, quantity, cart, pending);

    for (let attempt = 1; attempt <= retries; attempt += 1) {
      if (result.outcome !== 'error' || !result.retryable) break;
      logFallback('allocate failed — retrying', {
        variantId,
        attempt,
        of: retries,
        delayMs: retryDelayMs,
      });
      if (retryDelayMs > 0) {
        await new Promise((resolve) => scope.setTimeout(resolve, retryDelayMs));
      }
      result = await allocate(variantId, quantity, cart, pending);
    }

    return result;
  }

  /**
   * A synthetic 422 so every surface runs its existing cart-error path — that
   * shows the message, clears the loading state, and leaves the cart untouched.
   * @param {string} description
   */
  function rejectResponse(description) {
    return new Response(
      JSON.stringify({ status: 422, message: 'Cart Error', description }),
      { status: 422, statusText: 'Unprocessable Entity', headers: { 'Content-Type': 'application/json' } }
    );
  }

  /** Copy for the barred fallback — nothing was added and the shopper is told. */
  function cartErrorMessage() {
    return strings().cartError || 'Sorry, we could not add that. Please try again.';
  }

  /**
   * Drop what this page learned about the given variant ids. Both role caches
   * live for the page lifetime, which is wrong across a client-side sibling
   * swap: a stale 'plain' verdict makes resolveLines skip allocate for a real
   * base variant, and a stale 'base' verdict turns blocksFailOpen into a hard
   * error for an ordinary one. The swap code calls this with the INCOMING
   * product's variant ids so the next add re-derives their roles. Ids are
   * normalized to strings — the sets key on String(variantId) everywhere.
   * @param {Iterable<unknown>} ids
   */
  function forgetVariants(ids) {
    for (const id of ids || []) {
      const key = String(id);
      knownBaseVariantIds.delete(key);
      knownPlainVariantIds.delete(key);
    }
  }

  /**
   * A line the theme has positively identified as a base variant: a 'base' role
   * marker on this request, a Liquid base flag, or a successful allocation
   * earlier (this request or this page). Anything else is UNKNOWN — most likely
   * an ordinary product whose allocate 404 we never got to see.
   * @param {object} line
   * @returns {boolean}
   */
  function isKnownBaseLine(line) {
    const l = /** @type {any} */ (line);
    return l.stackRole === 'base' || knownBaseVariantIds.has(String(l.id));
  }

  /**
   * Why, if at all, the base-variant fallback must NOT happen for these lines.
   *
   *   'family-in-cart' — the cart already holds stack lines of a family being
   *     added, so a base line on top would oversell. When the cart could not be
   *     read we cannot prove it wouldn't, so every known base line counts as
   *     blocked; an unknown id (i.e. an ordinary product) still falls open.
   *   'policy' — the theme's failure mode is 'reject' and a known base line is
   *     involved. Unknown lines are exempt on purpose: see the module header.
   *
   * Evaluated in that order so the log names the stronger reason.
   * @param {any} cart Parsed /cart.js, or null when the read failed.
   * @param {Array<object>} lines
   * @returns {'family-in-cart'|'policy'|null}
   */
  function fallbackBarredReason(cart, lines) {
    const familyInCart = lines.some((line) =>
      cart ? familyUnitsInCart(cart, /** @type {any} */ (line).id) > 0 : isKnownBaseLine(line)
    );
    if (familyInCart) return 'family-in-cart';
    if (settings().failureMode === 'reject' && lines.some(isKnownBaseLine)) return 'policy';
    return null;
  }

  /**
   * Log a barred fallback in one consistent shape, keyed on its reason.
   * @param {'family-in-cart'|'policy'} reason
   * @param {object} [context]
   */
  function logBarredFallback(reason, context) {
    logFallback(
      reason === 'policy'
        ? 'fallback barred by theme setting (failure mode: reject) — adding nothing'
        : 'fallback barred: the cart already holds this family — adding nothing',
      context
    );
  }

  /**
   * Resolve every line, asking allocate about ids we don't already know.
   *
   * The cart is read once per pass, lazily, immediately before the first allocate
   * call — so a request made up entirely of ordinary products costs no cart read,
   * and the 422 retry (a second pass) always re-reads.
   * @returns {Promise<{status: 'passthrough'|'rejected'|'ready', items?: Array<object>, partial?: object, description?: string, failed?: boolean, cart?: any}>}
   */
  async function resolveLines(lines, options) {
    scanBaseFlags();

    /** @type {Array<object>} */
    const items = [];
    let swapped = false;
    /** @type {{requested: number, allocated: number}|null} */
    let partial = null;
    /** @type {any} */
    let cart = null;
    let cartRead = false;
    /**
     * Stack units this pass has committed to that are not in the cart yet: one
     * bundle can hold two lines of the same family, and the second allocate would
     * otherwise be handed the units the first one just took.
     * @type {Array<string>}
     */
    const pending = [];

    for (const line of lines) {
      if (line.stackRole === 'plain') {
        knownPlainVariantIds.add(line.id);
        items.push(toCartItem(line));
        continue;
      }

      if (!options?.forceRefresh && knownPlainVariantIds.has(line.id)) {
        items.push(toCartItem(line));
        continue;
      }

      if (line.stackRole === 'base') {
        knownBaseVariantIds.add(line.id);
      }

      if (!cartRead) {
        cart = await readCart();
        cartRead = true;
      }

      const result = await allocateWithRetry(line.id, line.quantity, cart, pending);

      if (result.outcome === 'plain') {
        knownPlainVariantIds.add(line.id);
        items.push(toCartItem(line));
        continue;
      }
      // Fail open, subject to the family guard the caller applies.
      if (result.outcome === 'error') return { status: 'passthrough', failed: true, cart };
      if (result.outcome === 'unfulfillable' || result.outcome === 'cartLimit') {
        // Neither may fall back: the base variant would be a second sold-out
        // rejection, or — for CART_LIMIT — a straight oversell.
        const atCartLimit = result.outcome === 'cartLimit';
        const message = atCartLimit
          ? strings().cartLimit || 'The maximum quantity of this item is already in your cart.'
          : strings().unfulfillable || 'Sorry, that just sold out.';
        logFallback(
          atCartLimit
            ? 'the cart already holds every available unit — adding nothing'
            : 'allocate says out of stock — adding nothing',
          { variantId: line.id, body: result.body }
        );
        return { status: 'rejected', description: message, cart, items };
      }

      knownBaseVariantIds.add(line.id);
      swapped = true;
      if (result.verdict === 'partial') {
        // Several base lines in one request can each fall short. Report the total
        // shortfall rather than whichever line happened to resolve last.
        partial = {
          requested: (partial?.requested || 0) + result.body.requested,
          allocated: (partial?.allocated || 0) + result.body.allocated,
        };
      }
      const expanded = expandLine(line, result.body);
      for (const item of expanded) pending.push(`${item.id}:${item.quantity}`);
      items.push(...expanded);
    }

    // Nothing in this request was a base variant: let the caller's original
    // request run verbatim rather than re-shaping a payload for no reason.
    if (!swapped) return { status: 'passthrough', cart, items };

    return { status: 'ready', items, partial: partial || undefined, cart };
  }

  /**
   * @param {string} url
   * @param {Array<object>} items
   * @param {Record<string, string>} passthrough
   */
  function postCartAdd(url, items, passthrough) {
    return originalFetch(toCartAddJsUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(buildCartAddBody(items, passthrough)),
    });
  }

  /**
   * Surface a partial add through the theme's own cart-notice path
   * (assets/cart-add-feedback.js), which re-reads the cart and dispatches
   * `cart:update` with an ABSOLUTE item count plus the flash message. Writing
   * `__bonobosPendingCartFlash` alone is not enough on either count: the drawer
   * clears an undated flash, and the surface that started the add reports the
   * quantity it *asked* for, which the cart icon adds as a delta.
   *
   * Deferred on purpose. The initiating surface dispatches its own `cart:update`
   * from the microtask chain that awaits our response, so a macrotask is what
   * guarantees the corrective absolute count lands last and wins.
   *
   * The count is substituted for a sentinel rather than a Liquid `{{ }}`
   * placeholder, so we never depend on how the `t` filter treats an unsupplied
   * interpolation.
   */
  function noticePartial(partial) {
    const template = strings().partial;
    if (!template) return;
    const message = String(template).replace(/__QTY__/g, String(partial.allocated));

    scope.setTimeout(() => {
      const feedback = /** @type {any} */ (scope).BonobosCartAddFeedback;
      if (typeof feedback?.announceCartAddNotice !== 'function') {
        // Nothing to announce through — say so rather than leaving a half-written
        // flash the drawer would silently discard.
        logFallback('partial add could not be announced — cart feedback unavailable', {
          message,
        });
        return;
      }
      Promise.resolve(feedback.announceCartAddNotice({ message, sourceId: 'product-stack' })).catch(
        (error) => logFallback('partial add announcement failed', { error: String(error) })
      );
    }, 0);
  }

  /**
   * @param {string} url
   * @param {{lines: Array<object>, passthrough: Record<string, string>}} parsed
   * @param {() => Promise<Response>} replayOriginal
   */
  async function hijack(url, parsed, replayOriginal) {
    /**
     * Re-issue a marker-stripped payload so `_product_stack_role` never reaches
     * Shopify and the caller's other properties survive intact. Always the
     * caller's OWN lines — a fail-open replay must never carry stack variants,
     * and it must never carry a partially resolved pass's line list either.
     */
    const replaySanitized = () =>
      postCartAdd(
        url,
        parsed.lines.map((line) => toCartItem(line)),
        parsed.passthrough
      );

    /**
     * The one fail-open door: replay the caller's own request unless the family
     * guard or the theme's failure mode bars it (see fallbackBarredReason).
     * @param {any} cart
     */
    const failOpen = (cart) => {
      const barred = fallbackBarredReason(cart, parsed.lines);
      if (barred) {
        logBarredFallback(barred, {
          variantIds: parsed.lines.map((line) => /** @type {any} */ (line).id),
        });
        return rejectResponse(cartErrorMessage());
      }
      if (parsed.hadRoleMarkers) return replaySanitized();
      return replayOriginal();
    };

    /** A pass that ended in 'passthrough': a fallback if it failed, else verbatim. */
    const settle = (resolution) =>
      resolution.failed
        ? failOpen(resolution.cart)
        : parsed.hadRoleMarkers
          ? replaySanitized()
          : replayOriginal();

    const resolved = await resolveLines(parsed.lines);

    if (resolved.status === 'passthrough') return settle(resolved);
    if (resolved.status === 'rejected') return rejectResponse(resolved.description || '');

    let response = await postCartAdd(url, resolved.items, parsed.passthrough);

    // The allocation is advisory: `consumed` removes the systematic repeat, not a
    // racy one — another shopper, a double click, a second tab — and stock is read
    // live but cached ~5s with nothing held. Re-allocate once, and note that
    // resolveLines re-reads the cart, which is essential: the rejected add is NOT
    // in the cart, so reusing the first snapshot would double-count.
    // Marked-plain adds already proved the family is not base — skip the retry.
    if (response.status === 422 && !allLinesMarkedPlain(parsed.lines)) {
      logFallback('cart/add rejected a stack variant — re-allocating once', {
        status: response.status,
      });
      const retried = await resolveLines(parsed.lines, { forceRefresh: true });

      if (retried.status === 'rejected') return rejectResponse(retried.description || '');
      if (retried.status !== 'ready') return settle(retried);

      response = await postCartAdd(url, retried.items, parsed.passthrough);
      if (response.status === 422) {
        logFallback('cart/add rejected the retry too', { status: response.status });
        return failOpen(retried.cart);
      }
      // Only announce a shortfall on an add that actually landed: the 422 above
      // is not the only way this can fail, and a 5xx here would otherwise tell
      // the shopper we added units that never reached the cart.
      if (response.ok && retried.partial) noticePartial(retried.partial);
      return response;
    }

    if (response.ok && resolved.partial) noticePartial(resolved.partial);
    return response;
  }

  /* -- fetch wrapper ------------------------------------------------------- */

  /** @type {any} */ (scope).fetch = async function productStackFetch(input, init) {
    const isRequest = typeof Request !== 'undefined' && input instanceof Request;
    const url = isRequest ? input.url : String(input ?? '');

    if (!isCartAddUrl(url)) return originalFetch(input, init);

    const method = String(
      (isRequest ? input.method : init?.method) || (isRequest ? 'GET' : 'GET')
    ).toUpperCase();
    if (method !== 'POST') return originalFetch(input, init);

    const replayOriginal = () => originalFetch(input, init);

    /** @type {{lines: Array<object>, passthrough: Record<string, string>, hadRoleMarkers: boolean} | null} */
    let parsed = null;

    try {
      if (isRequest) {
        // A Request body can only be read once — never touch the original.
        const clone = input.clone();
        const contentType = clone.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
          parsed = parseJsonPayload(await clone.json());
        } else {
          parsed = parseFormEntries(await clone.formData());
        }
      } else {
        const body = init?.body;
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          parsed = parseFormEntries(body);
        } else if (typeof body === 'string') {
          parsed = parseJsonPayload(JSON.parse(body));
        } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
          parsed = parseFormEntries(body);
        }
      }

      if (!parsed || parsed.lines.length === 0) return replayOriginal();

      return await hijack(url, parsed, replayOriginal);
    } catch (error) {
      logFallback('hijack threw', { error: String(error) });
      // The family guard covers this path too — an exception is no reason to put a
      // base line on top of the family's stack lines.
      if (parsed && parsed.lines.length > 0) {
        const cart = await readCart();
        const barred = fallbackBarredReason(cart, parsed.lines);
        if (barred) {
          logBarredFallback(barred, { error: String(error) });
          return rejectResponse(cartErrorMessage());
        }
      }
      return replayOriginal();
    }
  };

  /* -- non-AJAX form submits ----------------------------------------------- */

  // Bubble phase, and skipped when something already called preventDefault.
  // Every AJAX surface in this theme preventDefaults its own submit and then
  // fetches, so those are handled by the wrapper above — this only catches a
  // genuine browser form POST. A capture-phase listener would instead race
  // quick-add's own window-level capture listener for the same event.
  scope.addEventListener(
    'submit',
    (event) => {
      try {
        if (event.defaultPrevented) return;
        const form = /** @type {any} */ (event.target);
        if (!form || String(form.tagName).toLowerCase() !== 'form') return;

        const action = form.getAttribute('action') || '';
        if (!isCartAddUrl(action)) return;

        const parsed = parseFormEntries(new FormData(form));
        if (parsed.lines.length === 0) return;

        event.preventDefault();

        const cartAddUrl = scope.Theme?.routes?.cart_add_url || '/cart/add.js';
        // The outcome is final once the browser is navigating (form.submit()
        // started a real POST, and a location.assign after it would supersede
        // and abort that POST) or once we deliberately rejected the add. Either
        // way nothing below may submit or navigate again.
        let settled = false;
        const replayOriginal = () => {
          // Give up on interception and let the browser do the native POST.
          settled = true;
          form.submit();
          return Promise.resolve(new Response(null, { status: 204 }));
        };

        hijack(cartAddUrl, parsed, replayOriginal)
          .then(async (response) => {
            if (settled) return;

            if (!response.ok) {
              // A synthetic 422: sold out, cart limit, or a barred fallback.
              // Stay on the page so the reason is readable — navigating to the
              // cart would show an unchanged cart and no explanation.
              settled = true;
              const body = await response.json().catch(() => ({}));
              const feedback = /** @type {any} */ (scope).BonobosCartAddFeedback;
              const errorEl =
                form.querySelector('[data-cart-add-error]') ||
                form.querySelector('[ref="addToCartTextError"]');
              feedback?.showInlineCartError?.(errorEl, body.description || cartErrorMessage());
              return;
            }

            // Mirror the native flow: a full page POST lands on the cart.
            settled = true;
            scope.location.assign(scope.Theme?.routes?.cart_url || '/cart');
          })
          .catch(async (error) => {
            logFallback('form hijack failed', { error: String(error) });
            // The browser is already POSTing, or the add was deliberately
            // rejected and the message is on screen — either way, resubmitting
            // here would undo that.
            if (settled) return;
            // Guarded like every other fallback. A native POST has no notice
            // element to write to, so a barred fallback just leaves the page as
            // it is rather than adding a base line over the family's stack lines.
            const cart = await readCart();
            const barred = fallbackBarredReason(cart, parsed.lines);
            if (barred) {
              logBarredFallback(barred, { error: String(error) });
              return;
            }
            form.submit();
          });
      } catch (error) {
        logFallback('form submit hook threw — leaving the submit alone', {
          error: String(error),
        });
      }
    },
    false
  );

  const api = { installed: true, knownBaseVariantIds, knownPlainVariantIds, allocate, hijack, forgetVariants };
  /** @type {any} */ (scope)[GLOBAL_KEY] = api;
  return api;
}

// Progressive enhancement: if anything here throws the store must still work,
// so installation is guarded and never rethrows.
if (typeof window !== 'undefined') {
  try {
    install(window);
  } catch (error) {
    console.warn('[ProductStack] not installed — add to cart stays native', error);
  }
}

export default { isCartAddUrl, parseFormEntries, parseJsonPayload, classifyAllocation, expandLine, toCartItem, buildCartAddBody, buildConsumed, familyUnitsInCart, allLinesMarkedPlain, normalizeSettings, isTransientAllocateFailure, DEFAULT_SETTINGS, PRODUCT_STACK_ROLE_PROPERTY };
